/* Copyright (c) 2020-present CloudIO Inc */

import {
  chipVariant,
  iconButtonSize,
} from 'componentregistry/registry/constants';
import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { Chips, defaultValue } from './Chips';

const ChipsManager: ComponentManagerFn<'Chips'> = (
  platform: Platform,
): ComponentManager<'Chips'> => {
  return {
    ...baseComponentManager<'Chips'>(platform, 'Chips'),
    type: 'Chips',
    name: 'Chips',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnDelete'] as ItemType[]).includes(childType),
    defaultValue,
    onPropertyChange: (property, value, itemData) => {
      let val: Partial<Chips> = { [property]: value };
      if (property === 'delimiterCharacter' && typeof value === 'string') {
        if (value.length > 1) {
          val = { [property]: value.substr(0, 1) };
        }
      }
      return val;
    },
    props: {
      viewAttribute: {
        label: 'View Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
      delimiterCharacter: {
        label: 'Delimiter Character',
        type: 'string',
        helpText: 'Specify a delimiter e.g. | or ; or , etc.',
        category: 'Data',
        maxLength: 1,
      },
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        required: true,
        filterColor: (color: string) => {
          return ['default', 'primary', 'secondary'].includes(color);
        },
      },
      size: {
        label: 'Size',
        type: 'select',
        options: iconButtonSize,
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: chipVariant,
      },
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.viewAttribute ?? ' ',
    category: 'Data',
    icon: { iconName: 'rectangle-wide' },
    description: `**Overview**<br/>
The Chips component converts delimited string data into a set of visual tokens (tags). Instead of displaying a raw, comma-separated list like "Finance, HR, Operations", the component renders each item as a distinct, styled label. This dramatically improves readability and is commonly used for displaying tags, categories, multi-select values, or filter criteria.

**1. Data Source & Parsing**<br/>
The core function of this component is to split a single string into multiple visual elements.
* **View Attribute:** Select the data attribute that contains the string value (e.g., a field named "userRoles" or "projectTags").
* **Delimiter Character:** Defines the character used to separate values in the raw string.
* **Example:** If your data is stored as "Apple,Banana,Orange", set the delimiter to ",". The component will then render three separate chips: [Apple] [Banana] [Orange].

**2. Visual Customization**<br/>
You can tailor the appearance of the chips to match the importance of the data.
* **Color:** Sets the theme color of the chip background/border (e.g., Primary, Secondary, Default).
* **Variant:** Controls the visual style. (Filled: Solid background color for high emphasis; Outlined: Transparent background with a colored border for medium emphasis).
* **Size:** Adjusts the physical dimensions of the chips (e.g., Small, Medium).

**3. Interaction (Deletion)**<br/>
While typically read-only, Chips can be interactive.
* **TriggerOnDelete:** By adding a TriggerOnDelete component as a child, you enable a "Close" (X) icon on each chip.
* **Workflow:** When a user clicks the "X", the trigger fires. You can bind this to logic that removes the specific value from the underlying data string, effectively creating a tag editor.

**4. Logic & Control**<br/>
* **Disable Condition:** A boolean expression (e.g., \`$.user.role !== 'Admin'\`). If true, the chips become non-interactive (the delete action is disabled), useful for read-only modes.
* **Tooltip:** Adds hover text to the chips for additional context.`,
  };
};

export default ChipsManager;
